import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import DashboardStats from "./DashboardStats";
import TradingCalendar from "./TradingCalendar";
import AIOverview, { aiCache } from "./AIOverview";
import AddTradeModal from "./AddTradeModal";
import DayReviewModal, { reviewCache } from "./DayReviewModal";
import { getStats, getCalendar, getTradesByDate, getWeeklySummary } from "../services/api";

function getMonthString(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function clearCacheForDate(date) {
  delete reviewCache[date];
  delete aiCache[date];
  delete aiCache["dashboard"];
}

function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [tradeData, setTradeData] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddTradeModal, setShowAddTradeModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);

  async function refreshDashboard() {
    const [statsData, calendarData] = await Promise.all([
      getStats(),
      getCalendar(getMonthString(currentDate)),
    ]);
    setStats(statsData);
    setCalendarDays(calendarData.days || []);
  }

  async function handleDayClick(date) {
    try {
      setSelectedDate(date);
      setError("");
      const [dayData, weekData] = await Promise.all([
        getTradesByDate(date),
        getWeeklySummary(date),
      ]);
      setTradeData(dayData);
      setWeeklySummary(weekData);
      setShowDayModal(true);
    } catch {
      setError("Failed to load selected day data.");
    }
  }

  async function handleTradeSaved(date) {
    try {
      setShowAddTradeModal(false);
      // Clear cache so AI re-fetches with the new trade included
      clearCacheForDate(date);
      await refreshDashboard();
      await handleDayClick(date);
    } catch {
      setError("Failed to refresh after saving trade.");
    }
  }

  async function handleTradeDeleted(date) {
    try {
      // Clear cache so AI re-fetches with the deleted trade removed
      clearCacheForDate(date);
      await refreshDashboard();
      const [dayData, weekData] = await Promise.all([
        getTradesByDate(date),
        getWeeklySummary(date),
      ]);
      setTradeData(dayData);
      setWeeklySummary(weekData);
    } catch {
      setError("Failed to refresh after deleting trade.");
    }
  }

  useEffect(() => {
    async function loadDashboard() {
      try {
        setLoading(true);
        setError("");
        await refreshDashboard();
      } catch {
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, [currentDate]); // eslint-disable-line

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content-area">
        <div className="page-header">
          <div>
            <h1 className="page-title">Trading Journal Dashboard</h1>
            <p className="page-subtitle">
              Track trades, review screenshots, and improve your decision making.
            </p>
          </div>
          <button className="primary-button" onClick={() => setShowAddTradeModal(true)}>
            + Add Trade
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <p className="loading-text">Loading dashboard...</p>
        ) : (
          <>
            <DashboardStats stats={stats} />

            <div className="ai-overview-row">
              <AIOverview selectedDate={selectedDate} />
            </div>

            <div className="calendar-row">
              <TradingCalendar
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                calendarDays={calendarDays}
                selectedDate={selectedDate}
                onDayClick={handleDayClick}
              />
            </div>
          </>
        )}
      </main>

      {showDayModal && (
        <DayReviewModal
          selectedDate={selectedDate}
          tradeData={tradeData}
          weeklySummary={weeklySummary}
          onClose={() => setShowDayModal(false)}
          onAddTrade={() => { setShowDayModal(false); setShowAddTradeModal(true); }}
          onTradeDeleted={handleTradeDeleted}
        />
      )}

      {showAddTradeModal && (
        <AddTradeModal
          selectedDate={selectedDate}
          onClose={() => setShowAddTradeModal(false)}
          onSaved={handleTradeSaved}
        />
      )}
    </div>
  );
}

export default DashboardPage;